// Client-side technical indicator math for chart overlays. Operates on plain
// arrays (same length as the input bars, null before warmup) so callers can
// zip the result back onto bar.time to build lightweight-charts series data.
// Conventions match the Go backend (handlers/smart_entry.go): Wilder RMA for
// RSI, SMA-seeded EMA for MACD.

export function sma(values, period) {
  const out = new Array(values.length).fill(null);
  let sum = 0;
  for (let i = 0; i < values.length; i++) {
    sum += values[i];
    if (i >= period) sum -= values[i - period];
    if (i >= period - 1) out[i] = sum / period;
  }
  return out;
}

// EMA seeded by the SMA of the first `period` values (not the first value).
export function emaSmaSeeded(values, period) {
  const out = new Array(values.length).fill(null);
  if (values.length < period) return out;
  let sum = 0;
  for (let i = 0; i < period; i++) sum += values[i];
  let prev = sum / period;
  out[period - 1] = prev;
  const k = 2 / (period + 1);
  for (let i = period; i < values.length; i++) {
    prev = values[i] * k + prev * (1 - k);
    out[i] = prev;
  }
  return out;
}

export function rsiWilder(closes, period) {
  const out = new Array(closes.length).fill(null);
  if (closes.length <= period) return out;
  let gainSum = 0, lossSum = 0;
  for (let i = 1; i <= period; i++) {
    const diff = closes[i] - closes[i - 1];
    if (diff >= 0) gainSum += diff; else lossSum -= diff;
  }
  let avgGain = gainSum / period;
  let avgLoss = lossSum / period;
  out[period] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);
  for (let i = period + 1; i < closes.length; i++) {
    const diff = closes[i] - closes[i - 1];
    const gain = diff > 0 ? diff : 0;
    const loss = diff < 0 ? -diff : 0;
    avgGain = (avgGain * (period - 1) + gain) / period;
    avgLoss = (avgLoss * (period - 1) + loss) / period;
    out[i] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);
  }
  return out;
}

export function macd(closes, fastPeriod, slowPeriod, signalPeriod) {
  const emaFast = emaSmaSeeded(closes, fastPeriod);
  const emaSlow = emaSmaSeeded(closes, slowPeriod);
  const macdLine = closes.map((_, i) =>
    (emaFast[i] != null && emaSlow[i] != null) ? emaFast[i] - emaSlow[i] : null);

  const firstValid = macdLine.findIndex(v => v != null);
  const signalLine = new Array(closes.length).fill(null);
  if (firstValid !== -1 && closes.length - firstValid >= signalPeriod) {
    const tail = macdLine.slice(firstValid);
    const sig = emaSmaSeeded(tail, signalPeriod);
    for (let i = 0; i < sig.length; i++) signalLine[firstValid + i] = sig[i];
  }

  const hist = closes.map((_, i) =>
    (macdLine[i] != null && signalLine[i] != null) ? macdLine[i] - signalLine[i] : null);

  return { macdLine, signalLine, hist };
}

// Session-anchored VWAP — matches TradingView's Anchor=Session,
// Source=(H+L+C)/3 settings: cumulative sums reset at the first bar of each
// new ET calendar day, so it re-anchors every trading day rather than
// running forever across the whole fetched window.
export function vwapSession(bars) {
  const out = new Array(bars.length).fill(null);
  let cumPV = 0, cumV = 0, curDay = null;
  for (let i = 0; i < bars.length; i++) {
    const b = bars[i];
    const day = new Date(b.time * 1000).toLocaleDateString('en-US', { timeZone: 'America/New_York' });
    if (day !== curDay) { curDay = day; cumPV = 0; cumV = 0; }
    const typical = (b.high + b.low + b.close) / 3;
    const vol = b.volume || 0;
    cumPV += typical * vol;
    cumV += vol;
    out[i] = cumV > 0 ? cumPV / cumV : null;
  }
  return out;
}

// Zip a value array onto bar times, dropping the null warmup prefix.
export function toLineData(bars, values) {
  const out = [];
  for (let i = 0; i < bars.length; i++) {
    if (values[i] != null && !Number.isNaN(values[i])) out.push({ time: bars[i].time, value: values[i] });
  }
  return out;
}

export function toHistData(bars, values, colorFn) {
  const out = [];
  for (let i = 0; i < bars.length; i++) {
    if (values[i] != null && !Number.isNaN(values[i])) {
      out.push({ time: bars[i].time, value: values[i], color: colorFn(bars[i], values[i]) });
    }
  }
  return out;
}
